import { useMemo } from 'react';
import { useWorkflowStore } from '../stores/workflowStore';
import { useSchema } from '../contexts/SchemaContext';
import { Column } from '../types';

export interface InputColumn {
  column: Column;
  source: {
    nodeId: string;
    nodeType: string;
    nodeName: string;
  };
}

/**
 * 获取指定节点的输入列
 * @param nodeId 当前节点 ID
 * @param inputIndex 输入索引（0 表示第一个输入，1 表示第二个输入，以此类推），不传则返回所有输入
 * @returns 输入列数组或 undefined
 */
export function useNodeInputColumns(nodeId: string, inputIndex?: number): InputColumn[] | undefined {
  const { edges } = useWorkflowStore();
  const { availableSchemas, getNodeSchema } = useSchema();

  return useMemo(() => {
    // 获取指向当前节点的边（按输入顺序排序）
    const incomingEdges = edges
      .filter(edge => edge.target === nodeId)
      .sort((a, b) => {
        // 如果有多个输入，尝试按某种顺序排序
        // 这里简单按 source ID 排序以保证稳定性
        return a.source.localeCompare(b.source);
      });

    if (incomingEdges.length === 0) {
      return undefined;
    }

    // 如果指定了 inputIndex，只返回该索引对应的输入
    if (inputIndex !== undefined) {
      const edge = incomingEdges[inputIndex];
      if (!edge) {
        return undefined;
      }
      
      const schema = getNodeSchema(edge.source);
      if (!schema) {
        return undefined;
      }

      return schema.columns.map(column => ({
        column,
        source: {
          nodeId: schema.nodeId,
          nodeType: schema.nodeType,
          nodeName: schema.nodeName,
        },
      }));
    }

    // 返回所有输入的列（合并）
    const allColumns: InputColumn[] = [];
    incomingEdges.forEach(edge => {
      const schema = getNodeSchema(edge.source);
      if (schema) {
        schema.columns.forEach(column => {
          allColumns.push({
            column,
            source: {
              nodeId: schema.nodeId,
              nodeType: schema.nodeType,
              nodeName: schema.nodeName,
            },
          });
        });
      }
    });

    return allColumns.length > 0 ? allColumns : undefined;
  }, [nodeId, inputIndex, edges, availableSchemas, getNodeSchema]);
}

/**
 * 获取指定节点的所有输入 schemas
 * @param nodeId 当前节点 ID
 * @returns 输入 schema 数组
 */
export function useNodeInputSchemas(nodeId: string) {
  const { edges } = useWorkflowStore();
  const { getNodeSchema } = useSchema();

  return useMemo(() => {
    const incomingEdges = edges
      .filter(edge => edge.target === nodeId)
      .sort((a, b) => a.source.localeCompare(b.source));

    return incomingEdges.map(edge => {
      const schema = getNodeSchema(edge.source);
      return {
        edge,
        schema,
      };
    }).filter(item => item.schema !== undefined);
  }, [nodeId, edges, getNodeSchema]);
}
